import { isApiHttpError, requestEventStream } from './httpClient'
import type {
  NotificationEventEnvelope,
  NotificationEventType,
  PurchaseRequestEventEnvelope,
  PurchaseRequestEventType
} from './contracts'

export const SERVER_EVENTS_API_PREFIX = '/api/events'
const DEFAULT_RECONNECT_DELAY_MS = 1000

// A record keyed by the OpenAPI enum: a missing or unknown event type fails the type check.
const purchaseRequestEventTypeSet = {
  purchase_request_changed: true,
  invoice_payment_recorded: true,
  invoice_payment_reverted: true,
  invoice_receipt_recorded: true,
  invoice_receipt_reverted: true,
  invoice_receipt_documents_updated: true,
  invoice_payment_documents_updated: true,
  purchase_request_closed: true
} as const satisfies Record<PurchaseRequestEventType, true>

export const PURCHASE_REQUEST_EVENT_TYPES = Object.keys(purchaseRequestEventTypeSet) as readonly PurchaseRequestEventType[]

const notificationEventTypes = ['notification_created'] as const satisfies readonly NotificationEventType[]

export type ServerEventType = PurchaseRequestEventType | NotificationEventType

export type ServerEventEnvelope<TEventType extends ServerEventType = ServerEventType> =
  TEventType extends NotificationEventType ? NotificationEventEnvelope : PurchaseRequestEventEnvelope

function isPurchaseRequestEventType(value: unknown): value is PurchaseRequestEventType {
  return typeof value === 'string' && (PURCHASE_REQUEST_EVENT_TYPES as readonly string[]).includes(value)
}

function isNotificationEventType(value: unknown): value is NotificationEventType {
  return typeof value === 'string' && (notificationEventTypes as readonly string[]).includes(value)
}

function isOptionalInt32(value: unknown): boolean {
  return value === null || value === undefined || typeof value === 'number'
}

function isOptionalNullableString(value: unknown): boolean {
  return value === null || value === undefined || typeof value === 'string'
}

export interface ServerEventsConnection {
  close: () => void
  addEventListener: (type: string, listener: (event: Event) => void) => void
  removeEventListener: (type: string, listener: (event: Event) => void) => void
}

export type ServerEventsFactory = (url: string, init: EventSourceInit) => ServerEventsConnection

export interface ServerEventSubscription {
  close: () => void
}

function createDefaultEventSource(url: string, init: EventSourceInit): ServerEventsConnection {
  return new EventSource(url, init)
}

function buildServerEventsUrl(apiBaseUrl: string): string {
  return apiBaseUrl ? `${apiBaseUrl}${SERVER_EVENTS_API_PREFIX}` : SERVER_EVENTS_API_PREFIX
}

function isPurchaseRequestEventEnvelope(payload: unknown): payload is PurchaseRequestEventEnvelope {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const candidate = payload as {
    type?: unknown
    occurredAtUtc?: unknown
    payload?: {
      requestId?: unknown
      changedByUserId?: unknown
      changeKind?: unknown
      invoiceId?: unknown
      paymentId?: unknown
      receiptId?: unknown
      paymentProgress?: unknown
      fulfillmentProgress?: unknown
      currentStatus?: unknown
    }
  }

  if (!isPurchaseRequestEventType(candidate.type)
 || typeof candidate.occurredAtUtc !== 'string'
    || !candidate.payload
    || typeof candidate.payload.requestId !== 'number') {
    return false
  }

  const p = candidate.payload
  if (!(p.changedByUserId === null || p.changedByUserId === undefined || typeof p.changedByUserId === 'number')) {
    return false
  }

  if (!(p.changeKind === null || p.changeKind === undefined || typeof p.changeKind === 'string')) {
    return false
  }

  return isOptionalInt32(p.invoiceId)
    && isOptionalInt32(p.paymentId)
    && isOptionalInt32(p.receiptId)
    && isOptionalNullableString(p.paymentProgress)
    && isOptionalNullableString(p.fulfillmentProgress)
    && isOptionalNullableString(p.currentStatus)
}

function isNotificationEventEnvelope(payload: unknown): payload is NotificationEventEnvelope {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const candidate = payload as {
    type?: unknown
    occurredAtUtc?: unknown
    payload?: {
      notificationId?: unknown
      eventType?: unknown
      requestId?: unknown
      title?: unknown
    }
  }

  return isNotificationEventType(candidate.type)
    && typeof candidate.occurredAtUtc === 'string'
    && !!candidate.payload
    && typeof candidate.payload.notificationId === 'number'
    && typeof candidate.payload.eventType === 'string'
    && typeof candidate.payload.title === 'string'
    && isOptionalInt32(candidate.payload.requestId)
}

function parseServerEventPayload(rawPayload: string): ServerEventEnvelope {
  const parsed = JSON.parse(rawPayload) as unknown
  if (isPurchaseRequestEventEnvelope(parsed) || isNotificationEventEnvelope(parsed)) {
    return parsed
  }

  throw new Error('Некорректный формат события API.')
}

function isEnvelopeOfType<TEventType extends ServerEventType>(
  envelope: ServerEventEnvelope,
  eventType: TEventType
): envelope is ServerEventEnvelope<TEventType> {
  return envelope.type === eventType
}

export async function openServerEventsConnection(
  apiBaseUrl: string,
  options: {
    eventSourceFactory?: ServerEventsFactory
    signal?: AbortSignal
  } = {}
): Promise<ServerEventsConnection> {
  const url = buildServerEventsUrl(apiBaseUrl)
  const response = await requestEventStream(url, {
    signal: options.signal,
    fallbackErrorMessage: 'Не удалось подключиться к серверным событиям.'
  })

  const contentType = response.headers.get('content-type')?.toLowerCase() || ''
  void response.body?.cancel().catch(() => undefined)

  if (!contentType.includes('text/event-stream')) {
    throw new Error('Некорректный формат ответа API: ожидается text/event-stream.')
  }

  // The caller left while the handshake answered: no EventSource that would be closed right away.
  options.signal?.throwIfAborted()

  const eventSourceFactory = options.eventSourceFactory ?? createDefaultEventSource
  return eventSourceFactory(url, { withCredentials: true })
}

interface SharedSubscriber {
  eventType: ServerEventType
  onMessage: (event: ServerEventEnvelope) => void
  onError?: (error: unknown) => void
  onConnect?: () => void
}

interface SharedConnection {
  apiBaseUrl: string
  subscribers: Set<SharedSubscriber>
  eventSourceFactory?: ServerEventsFactory
  reconnectDelayMs: number
  isClosed: boolean
  isConnecting: boolean
  /** The handshake answered 401/403: no automatic reconnect, a new subscriber may try once more. */
  isDenied: boolean
  reconnectTimer: ReturnType<typeof setTimeout> | null
  abortController: AbortController | null
  connection: ServerEventsConnection | null
  typeListeners: Map<string, (event: Event) => void>
  errorListener: ((event: Event) => void) | null
}

/** One `EventSource` per API base URL for the whole tab: browsers allow only six HTTP/1.1 connections per host. */
const sharedConnections = new Map<string, SharedConnection>()

function snapshot(shared: SharedConnection): SharedSubscriber[] {
  return Array.from(shared.subscribers)
}

function notifyError(subscriber: SharedSubscriber, error: unknown): void {
  try {
    subscriber.onError?.(error)
  } catch {
    // A failing error handler of one subscriber must not stop the others.
  }
}

function notifyConnect(subscriber: SharedSubscriber): void {
  try {
    subscriber.onConnect?.()
  } catch (error) {
    notifyError(subscriber, error)
  }
}

function dispatchMessage(shared: SharedConnection, listenedType: string, event: Event): void {
  const subscribers = snapshot(shared).filter((subscriber) => subscriber.eventType === listenedType)
  let parsed: ServerEventEnvelope
  try {
    parsed = parseServerEventPayload((event as MessageEvent<string>).data)
  } catch (error) {
    subscribers.forEach((subscriber) => notifyError(subscriber, error))
    return
  }

  for (const subscriber of subscribers) {
    if (!shared.subscribers.has(subscriber) || !isEnvelopeOfType(parsed, subscriber.eventType)) {
      continue
    }
    try {
      subscriber.onMessage(parsed)
    } catch (error) {
      notifyError(subscriber, error)
    }
  }
}

function ensureTypeListener(shared: SharedConnection, eventType: string): void {
  if (!shared.connection || shared.typeListeners.has(eventType)) {
    return
  }
  const listener = (event: Event): void => dispatchMessage(shared, eventType, event)
  shared.typeListeners.set(eventType, listener)
  shared.connection.addEventListener(eventType, listener)
}

function releaseTypeListener(shared: SharedConnection, eventType: string): void {
  const listener = shared.typeListeners.get(eventType)
  if (!listener || snapshot(shared).some((subscriber) => subscriber.eventType === eventType)) {
    return
  }
  shared.connection?.removeEventListener(eventType, listener)
  shared.typeListeners.delete(eventType)
}

function detachConnection(shared: SharedConnection): void {
  const connection = shared.connection
  if (connection) {
    shared.typeListeners.forEach((listener, eventType) => connection.removeEventListener(eventType, listener))
    if (shared.errorListener) {
      connection.removeEventListener('error', shared.errorListener)
    }
    connection.close()
  }
  shared.connection = null
  shared.typeListeners.clear()
  shared.errorListener = null
}

function scheduleReconnect(shared: SharedConnection): void {
  if (shared.isClosed || shared.reconnectTimer !== null) {
    return
  }
  shared.reconnectTimer = setTimeout(() => {
    shared.reconnectTimer = null
    void connectShared(shared)
  }, shared.reconnectDelayMs)
}

async function connectShared(shared: SharedConnection): Promise<void> {
  if (shared.isClosed) {
    return
  }

  shared.abortController?.abort()
  const abortController = new AbortController()
  shared.abortController = abortController
  shared.isConnecting = true
  shared.isDenied = false

  try {
    const connection = await openServerEventsConnection(shared.apiBaseUrl, {
      eventSourceFactory: shared.eventSourceFactory,
      signal: abortController.signal
    })

    if (shared.isClosed || shared.abortController !== abortController) {
      connection.close()
      return
    }

    shared.isConnecting = false
    shared.connection = connection
    const errorListener = (): void => {
      detachConnection(shared)
      scheduleReconnect(shared)
    }
    shared.errorListener = errorListener
    connection.addEventListener('error', errorListener)
    snapshot(shared).forEach((subscriber) => ensureTypeListener(shared, subscriber.eventType))
    snapshot(shared).forEach((subscriber) => {
      if (shared.subscribers.has(subscriber)) {
        notifyConnect(subscriber)
      }
    })
  } catch (error) {
    if (shared.isClosed || shared.abortController !== abortController) {
      return
    }
    shared.isConnecting = false
    snapshot(shared).forEach((subscriber) => notifyError(subscriber, error))

    if (isApiHttpError(error) && (error.status === 401 || error.status === 403)) {
      shared.isDenied = true
      return
    }

    scheduleReconnect(shared)
  }
}

function closeShared(shared: SharedConnection): void {
  shared.isClosed = true
  if (shared.reconnectTimer !== null) {
    clearTimeout(shared.reconnectTimer)
    shared.reconnectTimer = null
  }
  shared.abortController?.abort()
  shared.abortController = null
  detachConnection(shared)
  if (sharedConnections.get(shared.apiBaseUrl) === shared) {
    sharedConnections.delete(shared.apiBaseUrl)
  }
}

/** Test-only: closes and forgets every shared connection between tests. Never call it from app code. */
export function resetServerEventsForTests(): void {
  sharedConnections.forEach((shared) => {
    shared.subscribers.clear()
    closeShared(shared)
  })
  sharedConnections.clear()
}

/**
 * Subscribes to one event type of the tab's shared `/api/events` connection. The first subscriber of a base URL opens
 * the connection, the last `close()` closes it; messages go only to the subscribers of their type, and an exception in
 * one subscriber's `onMessage` goes to its own `onError` without stopping the others. `eventSourceFactory` and
 * `reconnectDelayMs` of the first subscriber apply to the shared connection; later subscribers' values are ignored.
 * After a 401/403 handshake the connection does not reconnect by itself; the next new subscriber tries once more.
 */
export function subscribeToServerEvent<TEventType extends ServerEventType>(
  apiBaseUrl: string,
  options: {
    eventType: TEventType
    onMessage: (event: ServerEventEnvelope<TEventType>) => void
    onError?: (error: unknown) => void
    /**
     * Called after every established connection, the first one and each reconnect. A subscriber that joins an already
     * open connection gets one call right away, so the first call is always «connected» and the next ones reconnects.
     */
    onConnect?: () => void
    eventSourceFactory?: ServerEventsFactory
    reconnectDelayMs?: number
  }
): ServerEventSubscription {
  let shared = sharedConnections.get(apiBaseUrl)
  const isNew = !shared
  if (!shared) {
    shared = {
      apiBaseUrl,
      subscribers: new Set(),
      eventSourceFactory: options.eventSourceFactory,
      reconnectDelayMs: options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS,
      isClosed: false,
      isConnecting: false,
      isDenied: false,
      reconnectTimer: null,
      abortController: null,
      connection: null,
      typeListeners: new Map(),
      errorListener: null
    }
    sharedConnections.set(apiBaseUrl, shared)
  }

  const subscriber: SharedSubscriber = {
    eventType: options.eventType,
    onMessage: options.onMessage as (event: ServerEventEnvelope) => void,
    onError: options.onError,
    onConnect: options.onConnect
  }
  shared.subscribers.add(subscriber)

  if (isNew || (shared.isDenied && !shared.isConnecting)) {
    void connectShared(shared)
  } else if (shared.connection) {
    ensureTypeListener(shared, subscriber.eventType)
    notifyConnect(subscriber)
  }

  const current = shared
  let isClosed = false
  return {
    close(): void {
      if (isClosed) {
        return
      }
      isClosed = true
      current.subscribers.delete(subscriber)
      if (current.subscribers.size === 0) {
        closeShared(current)
        return
      }
      releaseTypeListener(current, subscriber.eventType)
    }
  }
}
